import React, { useState, useRef, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  fixedWidthCustomStyle,
  statusOptions,
} from "components/Common/constants";
import CommonTable from "components/ReactTable/Table";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentTermList } from "actions/customer";
import { DEFAULT_RECORDS_PER_PAGE } from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { dashboardOdooCustomerNotSyncedList } from "actions/dashboard";
import { useNavigate } from "react-router-dom";

function OdooCustomersList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Payment term list still comes from Redux
  const paymentTermOptions = useSelector(
    (state) => state.customer.paymentTermList?.data?.paymentTerm || []
  );

  // ✅ Store the Odoo customers response in local state
  const [odooResponse, setOdooResponse] = useState(null);

  const [filterState, setFilterState] = useState({
    searchName: "",
    searchPaymentTerm: null,
    searchStatus: null,
    filterParams: [],
  });

  const notificationAlertRef = useRef(null);
  const [pageNum, setPageNum] = useState(1);
  const [customerListState, setCustomerListState] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  // --- Build filter params from UI state ---
  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchPaymentTerm) {
      filterParams.push({
        field: "PaymentTermID",
        condition: "eq",
        filterValues: [filterState.searchPaymentTerm.value],
      });
    }
    if (filterState.searchStatus) {
      filterParams.push({
        field: "UserStatus",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return {
      filters:
        filterParams.length > 0 ? JSON.stringify(filterParams) : undefined,
      searchString: filterState.searchName || "",
    };
  };

  // --- Fetch Odoo not-synced customer list ---
  useEffect(() => {
    const fetchData = async () => {
      setCustomerListState((prev) => ({ ...prev, isLoading: true }));

      const payload = {
        pageNum: pageNum,
        searchString: filterState.searchName || "",
      };

      const f = filterState.filterParams.filters;
      if (f && f !== "[]" && typeof f === "string" && f.length > 2) {
        payload.filters = f;
      }

      try {
        const res = await dashboardOdooCustomerNotSyncedList(payload);
        // ✅ Save the full API response (with code, data.customer, etc.)
        setOdooResponse(res?.data || res);
      } catch (err) {
        setOdooResponse({
          code: 500,
          message: "Failed to load the customers list! Please try again.",
          data: null,
        });
      }
    };

    fetchData();
  }, [pageNum, filterState.filterParams]);

  // --- Fetch payment terms (for the filter dropdown) ---
  useEffect(() => {
    getPaymentTermList(dispatch);
  }, [dispatch]);

  // --- Sync loader / error state from the response ---
  useEffect(() => {
    if (!odooResponse) return;

    if (odooResponse.code === 200) {
      setCustomerListState({
        isLoading: false,
        error: "",
        noRecords: (odooResponse?.data?.customer?.length ?? 0) === 0,
        showLoaderOnClick: false,
      });
    } else if (odooResponse.code || odooResponse.message) {
      setCustomerListState({
        isLoading: false,
        error:
          odooResponse.message ||
          "Failed to load the customers list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [odooResponse]);

  // ✅ Formatted list reads from odooResponse.data.customer
  const formattedCustomerList =
    odooResponse?.data?.customer?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
          (odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) ?? [];

  const totalRecords = odooResponse?.data?.totalRecords || 0;
  const totalFilteredRecords = odooResponse?.data?.totalFilteredRecords || 0;
  const noOfRecordsPerPage = odooResponse?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE;

  const handleSearch = () => {
    if (
      !filterState.searchName.trim() &&
      !filterState.searchPaymentTerm &&
      !filterState.searchStatus
    ) {
      return;
    }

    const newfilterParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newfilterParams)
    ) {
      setCustomerListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
        isLoading: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClear = () => {
    setCustomerListState((prev) => ({ ...prev, isLoading: true }));
    setFilterState({
      searchName: "",
      searchPaymentTerm: null,
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  // --- Table columns ---
  const columns = [
    { Header: "Sr. no.", accessor: "srNo", centerAlign: true, className: "col-srNo" },
    {
      Header: "Name",
      accessor: "name",
      className: "col-cust-name",
      Cell: ({ row }) => {
        const { firstName = "", lastName = "" } = row?.original || {};
        const full = `${firstName} ${lastName}`.trim();
        return <span>{full || "-"}</span>;
      },
    },
    {
      Header: "Company Name",
      accessor: "companyName",
      className: "col-company",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Email",
      accessor: "email",
      className: "col-email",
      Cell: ({ value }) => (
        <div onClick={(e) => e.stopPropagation()}>{value || "-"}</div>
      ),
    },
    {
      Header: "Contact Number",
      accessor: "phoneNo",
      className: "col-phone",
      Cell: ({ value }) => (
        <div onClick={(e) => e.stopPropagation()}>{value || "-"}</div>
      ),
    },
    {
      Header: "Address",
      accessor: "address",
      className: "col-cust-address",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Payment Term",
      Cell: ({ row }) => row.original.paymentTerm?.term || "",
      className: "col-term",
    },
    {
      Header: "VAT ID",
      accessor: "vatID",
      className: "col-vat-client",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Client ID",
      accessor: "clientID",
      className: "col-vat-client",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Status",
      accessor: "status",
      className: "col-cust-status",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value || "-"}
        </Badge>
      ),
    },
    // {
    //   Header: "Action",
    //   accessor: "actions",
    //   className: "col-useraction",
    //   disableSortBy: true,
    //   Cell: ({ row }) => (
    //     <div className="actions-right d-flex justify-content-start">
    //       <div>
    //         <Button
    //           className="btn-icon icon-info"
    //           color="link"
    //           id={`edit${row.original.ID}`}
    //           size="sm"
    //           onClick={(e) => {
    //             e.stopPropagation();
    //             const encodedId = urlEncodedId(row.original.ID);
    //             window.open(
    //               `/admin/customers/${encodedId}/details?tab=details`,
    //               "_blank",
    //               "noopener,noreferrer"
    //             );
    //           }}
    //         >
    //           <i className="tim-icons icon-pencil tim-md" />
    //         </Button>
    //         <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
    //           Edit
    //         </UncontrolledTooltip>
    //       </div>
    //     </div>
    //   ),
    // },
  ];

  const Option = (props) => (
    <components.Option {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.Option>
  );

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color={props.data.value === "ACTIVE" ? "success" : "warning"} pill>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  return (
    <>
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
               <CardHeader className="mb-2">
                  <div className="d-flex flex-column align-items-start">
                              
                      <CardTitle tag="h3" className="font-weight-normal mb-0">
                        Odoo Customers - Not Synced
                      </CardTitle>
                  </div>
                </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name, email, VAT ID or client ID"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={paymentTermOptions.map((term) => ({
                        value: String(term.ID),
                        label: term.term,
                      }))}
                      placeholder="Payment term"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.searchPaymentTerm}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchPaymentTerm: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={statusOptions}
                      placeholder="Status"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {customerListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {(filterState.filterParams?.filters?.length > 0 ||
                      filterState.filterParams?.searchString?.trim()) && (
                      <Button
                        color="default"
                        className="d-flex align-items-center justify-content-center mt-2 mb-3 filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Record count */}
                <div className="mt-2 mb-3 mx-1">
                  {totalRecords > 0 && (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ||
                      filterState.filterParams?.searchString?.trim() ? (
                        <span className="font-weight-bold">
                          Showing {totalFilteredRecords} of {totalRecords} Customers
                        </span>
                      ) : (
                        <span className="font-weight-bold">
                          Total Customers : {totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Table */}
                <div className="table-responsive">
                  {customerListState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : customerListState.error ? (
                    <div className="loaderContainer text-muted d-flex flex-column">
                      <h4 className="text-muted">Failed to load customer list</h4>
                      <span className="validation-error">
                        {customerListState.error}
                      </span>
                    </div>
                  ) : customerListState.noRecords ? (
                    <div className="loaderContainer text-muted">
                      No customers found.
                    </div>
                  ) : (
                    <CommonTable
                      data={formattedCustomerList}
                      resizable={false}
                      columns={columns}
                      defaultPageSize={noOfRecordsPerPage}
                      activePage={pageNum}
                      showPaginationBottom={true}
                      className="-striped -highlight table-hover"
                      rowClick={(rowData) => {
                        const encodedId = urlEncodedId(rowData.ID);
                        window.open(
                          `/admin/customers/${encodedId}/details?tab=details`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      totalPages={Math.ceil(totalFilteredRecords / noOfRecordsPerPage)}
                      setPageNum={setPageNum}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default OdooCustomersList;